import { useEffect, useState } from "react";
import { Head, Link, router, usePage } from "@inertiajs/react";
import Layout from "../../components/Layout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Folder, ChevronLeft, Code, FileText, Eye, Loader2 } from "lucide-react";
import { formatHtml } from "../../lib/htmlToText";
import { cn } from "../../lib/utils";
import BasicInfo from "./BasicInfo";
import Creatives from "./Creatives";
import SubjectLines from "./SubjectLines";
import FromLines from "./FromLines";
import Footer from "./Footer";
import TrackingLinks from "./TrackingLinks";
import UnsubscribeLinks from "./UnsubscribeLinks";
import Discussions from "./Discussions";
import EditTemplateDialog from "../../components/EditTemplateDialog";
import { type PaginationMeta } from "../../components/TablePagination";
import { OFFER_TAB_PROPS } from "../../lib/offerTabs";

interface Template {
  id: number;
  name: string;
  html_content: string | null;
  text_content: string | null;
  folder_id: number;
  folder_name: string;
  uploaded_by: string;
  created_at: string;
}

interface TemplateFolder {
  id: number;
  name: string;
  description: string;
  templates_count: number;
}

interface Offer {
  id: number;
  name: string;
  client: string;
  status: string;
  vertical: string;
  country: string;
  provider: string;
}

interface SubjectLine {
  id: number;
  description: string;
  created_at: string;
}

interface FromLine {
  id: number;
  description: string;
  tag_ids?: number[];
  tags?: Tag[];
  created_at: string;
}

interface Footer {
  id: number;
  description: string;
  created_at: string;
}

interface TrackingLink {
  id: number;
  link: string;
  created_at: string;
}

interface UnsubscribeLink {
  id: number;
  name?: string;
  link: string;
  created_at: string;
}

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface Discussion {
  id: number;
  title: string;
  content: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  comments_count: number;
  created_at: string;
  updated_at: string;
}

// Everything below marked optional is loaded per tab, so it is absent until
// that tab is opened. renderTabContent gates on it before rendering.
interface ShowProps {
  offer: Offer;
  templates?: Template[];
  template_folders?: TemplateFolder[];
  subject_lines?: SubjectLine[];
  from_lines?: FromLine[];
  footers?: Footer[];
  tracking_links?: TrackingLink[];
  unsubscribe_links?: UnsubscribeLink[];
  discussions?: Discussion[];
  tags: Tag[];
  subject_lines_pagination?: PaginationMeta;
  from_lines_pagination?: PaginationMeta;
  footers_pagination?: PaginationMeta;
  tracking_links_pagination?: PaginationMeta;
  unsubscribe_links_pagination?: PaginationMeta;
  all_subject_line_descriptions?: string[];
  all_from_line_descriptions?: string[];
  is_admin: boolean;
}

function Show({ offer, templates, template_folders, subject_lines, from_lines, footers, tracking_links, unsubscribe_links, discussions, tags, subject_lines_pagination, from_lines_pagination, footers_pagination, tracking_links_pagination, unsubscribe_links_pagination, all_subject_line_descriptions, all_from_line_descriptions, is_admin }: ShowProps) {
  // Get initial tab from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'basic_info';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"preview" | "html" | "text">(
    "preview"
  );
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Props for the tab on screen; absent until that tab has been fetched
  const { props: pageProps } = usePage();
  const tabProps = OFFER_TAB_PROPS[activeTab] ?? [];
  const tabReady = tabProps.every((key) => pageProps[key] !== undefined);

  // Fetch a tab the first time it is opened. Also covers the case where a
  // create or delete redirect drops the tab's props -- the data comes back
  // rather than the tab sitting empty.
  useEffect(() => {
    if (tabProps.length > 0 && !tabReady) {
      router.reload({ only: tabProps });
    }
  }, [activeTab, tabReady]);

  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.replaceState({}, '', url.toString());
  };

  const tabs = [
    { id: "basic_info", name: "Basic Info" },
    { id: "creatives", name: "Creatives" },
    { id: "subject_lines", name: "Subject Lines" },
    { id: "from_lines", name: "From Lines" },
    { id: "footer", name: "Footer" },
    { id: "tracking_links", name: "Tracking Links" },
    { id: "unsubscribe_links", name:"Unsubscribe Links"},
    { id: "discussions", name: "Discussions" },
  ];

  const handlePreviewTemplate = (template: Template) => {
    setPreviewTemplate(template);
    // Text-only templates should open on the Text tab, not an empty preview
    setPreviewTab(template.html_content ? "preview" : "text");
    setPreviewDialogOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    setEditTemplate(template);
    setEditDialogOpen(true);
  };

  const renderTabContent = () => {
    // This tab's data has not arrived yet -- the effect above is fetching it
    if (!tabReady) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading...
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case "basic_info":
        return <BasicInfo offer={offer} is_admin={is_admin} />;

      case "creatives":
        return (
          <Creatives
            offer={offer}
            templates={templates!}
            template_folders={template_folders!}
            tags={tags}
            is_admin={is_admin}
            onPreviewTemplate={handlePreviewTemplate}
            onEditTemplate={handleEditTemplate}
          />
        );

      case "subject_lines":
        return (
          <SubjectLines
            offer={offer}
            subject_lines={subject_lines!}
            pagination={subject_lines_pagination!}
            all_descriptions={all_subject_line_descriptions!}
            tags={tags}
            is_admin={is_admin}
          />
        );

      case "from_lines":
        return (
          <FromLines
            offer={offer}
            from_lines={from_lines!}
            pagination={from_lines_pagination!}
            all_descriptions={all_from_line_descriptions!}
            tags = {tags}
            is_admin={is_admin}
          />
        );

      case "footer":
        return (
          <Footer
            offer={offer}
            footers={footers!}
            pagination={footers_pagination!}
            is_admin={is_admin}
          />
        );

      case "tracking_links":
        return (
          <TrackingLinks
            offer={offer}
            tracking_links={tracking_links!}
            pagination={tracking_links_pagination!}
            is_admin={is_admin}
          />
        );

      case "unsubscribe_links":
        return (
          <UnsubscribeLinks
            offer={offer}
            unsubscribe_links={unsubscribe_links!}
            pagination={unsubscribe_links_pagination!}
            is_admin={is_admin}
          />
        );

      case "discussions":
        return (
          <Discussions
            offer={offer}
            discussions={discussions!}
            is_admin={is_admin}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Head title={`${offer.name} | Mailkit - Resolute`} />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/offers"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Offers
          </Link>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              {offer.name}
            </h1>
            <div className="flex items-center gap-3">
              <div className="w-px h-6 bg-gray-300"></div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {offer.client}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {offer.provider}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-10 border-b border-gray-200" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  whitespace-nowrap py-3 px-1 text-sm border-b-2 transition-all duration-200 cursor-pointer
                  ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600 font-semibold"
                      : "border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300 font-medium"
                  }
                `}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>{renderTabContent()}</div>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onOpenChange={setPreviewDialogOpen}
          modal={true}
        >
          <DialogContent
            className="!max-w-4xl w-full h-[85vh] p-0 overflow-hidden"
            showCloseButton={false}
          >
            <DialogHeader className="px-6 py-4 border-b">
              <DialogTitle className="text-xl font-semibold">
                {previewTemplate?.name || "Template Preview"}
              </DialogTitle>
              <DialogDescription>
                Preview of the email template
              </DialogDescription>
              <div className="flex items-center gap-1.5 pt-3">
                <button
                  onClick={() => setPreviewTab("preview")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    previewTab === "preview"
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50"
                  )}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Preview
                  {!previewTemplate?.html_content && (
                    <span className="text-[10px] text-slate-400">empty</span>
                  )}
                </button>
                <button
                  onClick={() => setPreviewTab("html")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    previewTab === "html"
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50"
                  )}
                >
                  <Code className="h-3.5 w-3.5" />
                  HTML
                  {!previewTemplate?.html_content && (
                    <span className="text-[10px] text-slate-400">empty</span>
                  )}
                </button>
                <button
                  onClick={() => setPreviewTab("text")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    previewTab === "text"
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50"
                  )}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Text
                  {!previewTemplate?.text_content?.trim() && (
                    <span className="text-[10px] text-slate-400">empty</span>
                  )}
                </button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-auto bg-gray-50 p-6">
              <div className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200">
                {previewTemplate && previewTab === "preview" && (
                  previewTemplate.html_content ? (
                    <iframe
                      srcDoc={previewTemplate.html_content}
                      className="w-full h-[600px] border-0 rounded-lg"
                      sandbox="allow-same-origin"
                      title="Email Template Preview"
                    />
                  ) : (
                    <div className="w-full h-[600px] flex items-center justify-center rounded-lg text-sm text-slate-400">
                      This template has no HTML part.
                    </div>
                  )
                )}
                {previewTemplate && previewTab === "html" && (
                  previewTemplate.html_content ? (
                    <pre className="w-full h-[600px] overflow-auto p-6 rounded-lg font-mono text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap break-words">
                      {formatHtml(previewTemplate.html_content)}
                    </pre>
                  ) : (
                    <div className="w-full h-[600px] flex items-center justify-center rounded-lg text-sm text-slate-400">
                      This template has no HTML part.
                    </div>
                  )
                )}
                {previewTemplate && previewTab === "text" && (
                  previewTemplate.text_content?.trim() ? (
                    <pre className="w-full h-[600px] overflow-auto p-6 rounded-lg font-mono text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap break-words">
                      {previewTemplate.text_content}
                    </pre>
                  ) : (
                    <div className="w-full h-[600px] flex items-center justify-center rounded-lg text-sm text-slate-400">
                      No text content for this template. Add it from the
                      template editor.
                    </div>
                  )
                )}
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t bg-white">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Folder className="w-4 h-4" />
                    {previewTemplate?.folder_name}
                  </span>
                  <span>
                    Created:{" "}
                    {previewTemplate &&
                      new Date(previewTemplate.created_at).toLocaleDateString()}
                  </span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPreviewDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Template Dialog */}
        <EditTemplateDialog
          template={editTemplate}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          offerId={offer.id}
        />
      </div>
    </div>
    </>
  );
}

Show.layout = (page) => <Layout>{page}</Layout>;

export default Show;
